/**
 * Image processing utilities for canvas operations
 */

export interface ImageTransform {
  rotation: number; // 0, 90, 180, 270
  flipHorizontal: boolean;
  flipVertical: boolean;
  brightness: number; // -100 to 100
  contrast: number; // -100 to 100
}

export interface Annotation {
  id: string;
  type: 'text' | 'arrow' | 'rectangle' | 'circle' | 'freehand';
  color: string;
  data: any;
}

/**
 * Apply transformations to a canvas
 */
export const applyTransformations = (
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  transform: ImageTransform
): void => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Calculate dimensions based on rotation
  const isRotated = transform.rotation === 90 || transform.rotation === 270;
  const displayWidth = isRotated ? img.naturalHeight : img.naturalWidth;
  const displayHeight = isRotated ? img.naturalWidth : img.naturalHeight;

  canvas.width = displayWidth;
  canvas.height = displayHeight;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();

  // Apply transformations
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((transform.rotation * Math.PI) / 180);

  const scaleX = transform.flipHorizontal ? -1 : 1;
  const scaleY = transform.flipVertical ? -1 : 1;
  ctx.scale(scaleX, scaleY);

  // Apply brightness and contrast filters
  const brightness = 100 + transform.brightness;
  const contrast = 100 + transform.contrast;
  ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;

  ctx.drawImage(
    img,
    -img.naturalWidth / 2,
    -img.naturalHeight / 2,
    img.naturalWidth,
    img.naturalHeight
  );

  ctx.restore();
};

/**
 * Draw annotations on canvas
 */
export const drawAnnotations = (
  canvas: HTMLCanvasElement,
  annotations: Annotation[]
): void => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  annotations.forEach((annotation) => {
    ctx.save();
    ctx.strokeStyle = annotation.color;
    ctx.fillStyle = annotation.color;
    ctx.lineWidth = annotation.data.thickness || 3;

    switch (annotation.type) {
      case 'text':
        ctx.font = `${annotation.data.fontSize || 16}px sans-serif`;
        ctx.fillText(annotation.data.text, annotation.data.x, annotation.data.y);
        break;

      case 'arrow':
        drawArrow(ctx, annotation.data, canvas);
        break;

      case 'rectangle':
        if (annotation.data.filled) {
          ctx.fillRect(
            annotation.data.x,
            annotation.data.y,
            annotation.data.width,
            annotation.data.height
          );
        } else {
          ctx.strokeRect(
            annotation.data.x,
            annotation.data.y,
            annotation.data.width,
            annotation.data.height
          );
        }
        break;

      case 'circle':
        ctx.beginPath();
        ctx.arc(
          annotation.data.x,
          annotation.data.y,
          annotation.data.radius,
          0,
          2 * Math.PI
        );
        if (annotation.data.filled) {
          ctx.fill();
        } else {
          ctx.stroke();
        }
        break;

      case 'freehand':
        ctx.beginPath();
        const points = annotation.data.points;
        if (points.length > 0) {
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
          }
          ctx.stroke();
        }
        break;
    }

    ctx.restore();
  });
};

/**
 * Draw preview of shape being drawn
 */
export const drawPreview = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  type: string,
  start: { x: number; y: number },
  current: { x: number; y: number },
  color: string,
  thickness: number
): void => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = `${color}33`; // Transparent fill
  ctx.lineWidth = thickness;
  ctx.setLineDash([5, 5]); // Dashed preview

  switch (type) {
    case 'arrow':
      drawArrow(ctx, { x1: start.x, y1: start.y, x2: current.x, y2: current.y, thickness }, canvas);
      break;

    case 'rectangle':
      const width = current.x - start.x;
      const height = current.y - start.y;
      ctx.strokeRect(start.x, start.y, width, height);
      break;

    case 'circle':
      const radius = Math.sqrt(
        Math.pow(current.x - start.x, 2) + Math.pow(current.y - start.y, 2)
      );
      ctx.beginPath();
      ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
      ctx.stroke();
      break;
  }

  ctx.restore();
};

/**
 * Draw an arrow on canvas
 */
const drawArrow = (
  ctx: CanvasRenderingContext2D,
  data: { x1: number; y1: number; x2: number; y2: number; thickness?: number },
  canvas?: HTMLCanvasElement
): void => {
  const { x1, y1, x2, y2, thickness = 3 } = data;
  
  // Scale arrow head proportionally with thickness and canvas size
  const baseSize = canvas ? Math.min(canvas.width, canvas.height) : 1000;
  const headLength = Math.max(20, Math.min(thickness * 8, baseSize * 0.02));
  
  const angle = Math.atan2(y2 - y1, x2 - x1);

  // Draw line
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Draw arrowhead
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLength * Math.cos(angle - Math.PI / 6),
    y2 - headLength * Math.sin(angle - Math.PI / 6)
  );
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLength * Math.cos(angle + Math.PI / 6),
    y2 - headLength * Math.sin(angle + Math.PI / 6)
  );
  ctx.stroke();
};

/**
 * Auto-enhance image using histogram equalization
 */
export const autoEnhance = (
  canvas: HTMLCanvasElement,
  img: HTMLImageElement
): ImageTransform => {
  // Simple auto-enhance: increase contrast slightly and adjust brightness
  return {
    rotation: 0,
    flipHorizontal: false,
    flipVertical: false,
    brightness: 10,
    contrast: 20,
  };
};

/**
 * Draw watermark on canvas
 */
export const drawWatermark = (
  canvas: HTMLCanvasElement,
  logoImg: HTMLImageElement | null,
  text: string,
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
  opacity: number,
  size: number
): void => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.save();
  ctx.globalAlpha = opacity;

  const padding = 20;
  let x = padding;
  let y = padding;

  switch (position) {
    case 'top-right':
      x = canvas.width - size - padding;
      y = padding;
      break;
    case 'bottom-left':
      x = padding;
      y = canvas.height - size - padding;
      break;
    case 'bottom-right':
      x = canvas.width - size - padding;
      y = canvas.height - size - padding;
      break;
  }

  if (logoImg && logoImg.complete) {
    ctx.drawImage(logoImg, x, y, size, size);
  } else {
    // Draw text watermark
    ctx.fillStyle = '#ffffff';
    ctx.font = `${size / 3}px sans-serif`;
    ctx.fillText(text, x, y + size / 2);
  }

  ctx.restore();
};

/**
 * Draw timestamp overlay
 */
export const drawTimestamp = (
  canvas: HTMLCanvasElement,
  date: Date,
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
  format: 'date' | 'time' | 'both'
): void => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  let text = '';
  switch (format) {
    case 'date':
      text = date.toLocaleDateString();
      break;
    case 'time':
      text = date.toLocaleTimeString();
      break;
    case 'both':
      text = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
      break;
  }

  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.font = '14px sans-serif';

  const metrics = ctx.measureText(text);
  const padding = 10;
  const textWidth = metrics.width + padding * 2;
  const textHeight = 30;

  let x = padding;
  let y = padding;

  switch (position) {
    case 'top-right':
      x = canvas.width - textWidth - padding;
      break;
    case 'bottom-left':
      y = canvas.height - textHeight - padding;
      break;
    case 'bottom-right':
      x = canvas.width - textWidth - padding;
      y = canvas.height - textHeight - padding;
      break;
  }

  ctx.fillRect(x, y, textWidth, textHeight);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, x + padding, y + 20);
  ctx.restore();
};

/**
 * Export canvas to blob
 */
export const canvasToBlob = (
  canvas: HTMLCanvasElement,
  quality: number = 0.9
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/jpeg',
      quality
    );
  });
};
